import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion, type Variants } from "framer-motion";

/**
 * "Date Night, Handled" lifestyle band (design 3b).
 *
 * Full-bleed band: one wide cinematic photo with a scrim + headline/CTA.
 * Desktop: scrim runs left→right, content sits on the left. Mobile: the band
 * becomes a 4:5 photo with a bottom→up scrim and a full-width CTA.
 *
 * The wide photo is served from /public (WIDE_BAND_SRC); swap that path to
 * change it. Keep the alt text stock-neutral (do NOT imply the people are
 * Cello clients).
 */
const WIDE_BAND_SRC = "/images/date-night-band.jpg";
const WIDE_BAND_ALT = "A couple holding hands by a fountain on a night out";

interface LifestyleBandBassProps {
  onGetStartedClick?: () => void;
  queued?: boolean;
  /** Splash pages: fit-content pill CTA on mobile (instead of full-width). */
  mobileRedesign?: boolean;
}

const Placeholder = ({ label, className }: { label: string; className?: string }) => (
  <div
    aria-hidden="true"
    className={`flex items-center justify-center bg-gradient-to-br from-[#B45309] to-[#78350F] ${className ?? ""}`}
  >
    <span className="px-4 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60">
      {label}
    </span>
  </div>
);

const LifestyleBandBass = ({ onGetStartedClick, queued = false, mobileRedesign = false }: LifestyleBandBassProps) => {
  const navigate = useNavigate();
  const reduce = !!useReducedMotion();

  const handleCta = () =>
    onGetStartedClick ? onGetStartedClick() : navigate("/auth", { state: { tab: "signup" } });

  const container: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
  };
  const item: Variants = reduce
    ? { hidden: { opacity: 0 }, visible: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 16 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
      };

  return (
    <section className="relative w-full overflow-hidden bg-[#FFF7F0]">
      {/* Band — mobile: 4:5 photo (scrim bottom→up); md+: fixed-height band (scrim left→right) */}
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#1c1917] md:bg-transparent md:aspect-auto md:h-[560px] lg:h-[720px]">
        {/* Wide photo */}
        <motion.div
          className="absolute inset-0 z-0"
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 1.03 }}
          whileInView={reduce ? { opacity: 1 } : { opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          {WIDE_BAND_SRC ? (
            <img src={WIDE_BAND_SRC} alt={WIDE_BAND_ALT} className="h-full w-full object-cover" />
          ) : (
            <Placeholder
              label="Wide lifestyle band · ≥2400px"
              className="h-full w-full"
            />
          )}
        </motion.div>

        {/* Scrim (photo → scrim → content). Exact spec stops via arbitrary gradient. */}
        <div className="pointer-events-none absolute inset-0 z-10 bg-[linear-gradient(to_top,rgba(0,0,0,0.75),rgba(0,0,0,0.3)_45%,transparent_75%)] md:bg-[linear-gradient(to_right,rgba(10,5,2,0.65),rgba(10,5,2,0.3)_40%,transparent_65%)]" />

        {/* Content */}
        <motion.div
          className="pointer-events-none absolute bottom-8 left-6 right-6 z-20 flex max-w-[460px] flex-col gap-[18px] md:gap-5 md:bottom-auto md:left-[64px] md:right-auto md:top-1/2 md:-translate-y-1/2 lg:left-[88px]"
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <motion.span
            variants={item}
            className="font-inter text-[12px] font-semibold uppercase tracking-[0.25em] text-[#FFD9B0]"
          >
            Date night, handled
          </motion.span>
          <motion.h2
            variants={item}
            className="font-playfair text-[36px] font-medium leading-[1.12] text-[#FFF7F0] md:text-[40px] lg:text-[50px]"
          >
            He didn&apos;t have to think about his <span className="italic">outfit</span> once.
          </motion.h2>
          <motion.div variants={item} className={mobileRedesign ? "pointer-events-auto w-auto" : "pointer-events-auto w-full md:w-auto"}>
            <Button
              onClick={handleCta}
              className={`${mobileRedesign ? "h-[52px] w-auto rounded-full px-8 text-[12px] tracking-[0.18em] md:px-9 md:text-[13px]" : "h-[52px] w-full rounded-full px-9 text-[13px] tracking-[0.18em] md:w-auto"} font-bold uppercase shadow-[0_3px_0_rgba(0,0,0,0.25)] transition-all focus-visible:ring-2 focus-visible:ring-[#FFD700] focus-visible:ring-offset-2 active:translate-y-[3px] active:shadow-none ${queued ? "bg-[#16A34A] hover:bg-[#15803D] text-white" : "bg-white hover:bg-stone-100 text-[#0f172a]"}`}
            >
              {queued ? "You're on the list" : "Join the waitlist"}
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default LifestyleBandBass;
